import { TypographyP2 } from '@heidi/design-system/typography';
import { AudioLines } from 'lucide-react';
import { useReducedMotion } from 'motion/react';
import * as m from 'motion/react-m';
import type { ReactElement, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

/**
 * Code-drawn illustration for the Session Merge instructional modal (Figma
 * 9334-31452): a green listening orb with pulsing rings, flanked by
 * "Full name" and "Date of birth" pills. Drawn in code rather than shipped as
 * an image asset so the pill texts localise. Mirrors iOS
 * `HHSessionMergeMatchingIllustration` (geometry, 7.6s pulse cadence, static
 * rings under reduced motion).
 */

const RING_COUNT = 3;
/** Half the transcribe-view pulse speed for a calmer, ambient feel (iOS parity). */
const RING_PULSE_DURATION_S = 7.6;
/** Pulses fade out at ~250px diameter, just past the outermost Figma ring. */
const RING_MAX_SCALE = 2.6;
const RING_START_SCALE = 1.1;
/**
 * Fraction of the pulse spent fading in. Pulses must be transparent at both
 * loop boundaries: opacity and scale wrap as separate value animations, so a
 * pulse visible at the boundary can flash at full size for a frame.
 */
const RING_FADE_IN_FRACTION = 0.15;
/** Scale at the fade-in peak, keeping scale growth linear across the cycle. */
const RING_FADE_IN_SCALE =
  RING_START_SCALE + RING_FADE_IN_FRACTION * (RING_MAX_SCALE - RING_START_SCALE);
/** Pill centre offsets from the orb centre, measured from the Figma artwork. */
const FULL_NAME_PILL_OFFSET = { x: -64, y: -41 };
const DATE_OF_BIRTH_PILL_OFFSET = { x: 89, y: 42 };

function IllustrationPill({
  offset,
  children,
}: {
  offset: { x: number; y: number };
  children: ReactNode;
}): ReactElement {
  return (
    <div
      className="absolute left-1/2 top-1/2 rounded-full bg-surface px-4 py-2 shadow-md"
      style={{
        transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))`,
      }}
    >
      <TypographyP2 className="whitespace-nowrap">{children}</TypographyP2>
    </div>
  );
}

function PulsingRings(): ReactElement {
  return (
    <>
      {Array.from({ length: RING_COUNT }, (_, i) => (
        <m.div
          key={i}
          data-testid="session-merge-illustration-pulse-ring"
          className="absolute size-24 rounded-full border-2 border-border"
          initial={{ scale: RING_START_SCALE, opacity: 0 }}
          animate={{
            scale: [RING_START_SCALE, RING_FADE_IN_SCALE, RING_MAX_SCALE],
            opacity: [0, 1, 0],
          }}
          transition={{
            duration: RING_PULSE_DURATION_S,
            times: [0, RING_FADE_IN_FRACTION, 1],
            repeat: Infinity,
            ease: 'linear',
            // Negative delay phase-offsets each ring so all are mid-pulse on
            // first paint instead of appearing one by one over the first cycle.
            delay: (-i * RING_PULSE_DURATION_S) / RING_COUNT,
          }}
        />
      ))}
    </>
  );
}

function StaticRings(): ReactElement {
  return (
    <>
      <div
        data-testid="session-merge-illustration-static-ring"
        className="absolute size-40 rounded-full border-2 border-border"
      />
      <div
        data-testid="session-merge-illustration-static-ring"
        className="absolute size-60 rounded-full border-2 border-border"
      />
    </>
  );
}

export function SessionMergeMatchingIllustration(): ReactElement {
  const reducedMotion = useReducedMotion();

  return (
    // Decorative: the pill texts restate the modal's supporting copy.
    <div
      aria-hidden
      data-testid="session-merge-matching-illustration"
      className="relative flex size-full items-center justify-center overflow-hidden"
    >
      {reducedMotion ? <StaticRings /> : <PulsingRings />}
      <div className="flex size-24 items-center justify-center rounded-full bg-validation-success-hover">
        <AudioLines className="size-10 text-text-primary-invert" />
      </div>
      <IllustrationPill offset={FULL_NAME_PILL_OFFSET}>
        <FormattedMessage defaultMessage="Full name" id="yk4PT9S4sc" />
      </IllustrationPill>
      <IllustrationPill offset={DATE_OF_BIRTH_PILL_OFFSET}>
        <FormattedMessage defaultMessage="Date of birth" id="GFTdXwO9W3" />
      </IllustrationPill>
    </div>
  );
}
